import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../utils/api';
import i18n from '../i18n';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        const parsedUser = JSON.parse(stored);
        setUser(parsedUser);
        if (parsedUser.languagePref) {
          i18n.changeLanguage(parsedUser.languagePref);
        }
      } catch (_err) {
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const completeSession = ({ user: userData }) => {
    localStorage.setItem('user', JSON.stringify(userData));

    setUser(userData);
    i18n.changeLanguage(userData.languagePref || 'fr');
    return userData;
  };

  const login = async (email, password, mfaMethod) => {
    const res = await authAPI.login({ email, password, mfaMethod });
    if (res.data?.mfaRequired) {
      return {
        mfaRequired: true,
        mfaToken: res.data.mfaToken,
        method: res.data.method,
        destination: res.data.destination,
        expiresAt: res.data.expiresAt,
      };
    }

    return completeSession(res.data);
  };

  const verifyMfa = async ({ mfaToken, code }) => {
    const res = await authAPI.mfaVerify({ mfaToken, code });
    return completeSession(res.data);
  };

  const resendMfaCode = async ({ mfaToken, method }) => {
    const res = await authAPI.mfaChallenge({ mfaToken, method });
    return {
      mfaRequired: true,
      mfaToken: res.data.mfaToken,
      method: res.data.method,
      destination: res.data.destination,
      expiresAt: res.data.expiresAt,
    };
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch {}
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (updates) => {
    const updated = { ...user, ...updates };
    setUser(updated);
    localStorage.setItem('user', JSON.stringify(updated));
  };

  const hasRole = (...roles) => roles.includes(user?.role);

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyMfa, resendMfaCode, logout, updateUser, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
